/**
 * Serial Gate — シリアルコード認証 JS
 *
 * Storefront Cart API でディスカウントコードの有効性を判定し、
 * 有効なら /discount/CODE でセッションに適用 → 購入ボタンを解放する。
 *
 * セキュリティ: クライアント側にコード一覧を持たない。
 * 判定は Shopify ディスカウント機構の応答 (applicable) に基づく。
 */
(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    var sections = document.querySelectorAll('[id^="serial-gate-"][data-storefront-token]');
    sections.forEach(function (section) { setupGate(section); });
  }

  function setupGate(section) {
    var sectionId = section.id.replace('serial-gate-', '');
    var token = section.dataset.storefrontToken;
    var shopDomain = section.dataset.shopDomain;
    var apiVersion = section.dataset.apiVersion || '2024-10';
    var productHandle = section.dataset.productHandle;
    var firstVariantNumericId = section.dataset.firstVariantId;
    var variantGid = firstVariantNumericId
      ? 'gid://shopify/ProductVariant/' + firstVariantNumericId
      : null;

    var input = document.getElementById('serial-gate-input-' + sectionId);
    var submitBtn = document.getElementById('serial-gate-submit-' + sectionId);
    var statusEl = document.getElementById('serial-gate-status-' + sectionId);

    if (!input || !submitBtn || !statusEl || !token) return;

    var settings = getSettings(section);
    var isValidating = false;
    var storageKey = 'serial-gate-validated-' + productHandle;

    // Mark as campaign page (hides site nav/footer)
    document.body.classList.add('serial-gate-page');

    // Lock buy buttons initially (CSS-based, Alpine-proof)
    lockBuyButtons();

    // 一人3点まで: 数量入力を最大3に制限（クライアント側の第一防衛。
    // 超過注文はサーバ側の注文検証スクリプトでも検出する）
    capQuantityInputs(3);

    // Check if already validated in this session
    if (sessionStorage.getItem(storageKey)) {
      unlockBuyButtons();
      showStatus('success', settings.successMessage);
      disableForm();
      return;
    }

    // QR auto-fill: ?code=XXXX
    var params = new URLSearchParams(window.location.search);
    var codeParam = params.get('code');
    if (codeParam) {
      input.value = codeParam.trim().toUpperCase();
    }

    // Submit handler
    submitBtn.addEventListener('click', handleSubmit);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    });

    // --- Core functions ---

    function handleSubmit() {
      if (isValidating) return;

      var code = input.value.trim().toUpperCase();
      if (!code) {
        showStatus('error', settings.errorEmpty);
        return;
      }

      isValidating = true;
      submitBtn.disabled = true;
      submitBtn.style.pointerEvents = 'none';
      input.disabled = true;
      showStatus('loading', '確認中...');

      validateCode(code, token, shopDomain, apiVersion, variantGid)
        .then(function (result) {
          if (result.applicable) {
            // Apply discount to session via /discount/ endpoint (no page reload)
            return fetch('/discount/' + encodeURIComponent(code), {
              method: 'GET',
              redirect: 'follow',
              credentials: 'same-origin'
            }).then(function () {
              // Discount cookie is now set for checkout
              sessionStorage.setItem(storageKey, code);
              unlockBuyButtons();
              showStatus('success', settings.successMessage);
              disableForm();
            });
          } else {
            showStatus('error', settings.errorInvalid);
            isValidating = false;
            submitBtn.disabled = false;
            submitBtn.style.pointerEvents = '';
            input.disabled = false;
          }
        })
        .catch(function (err) {
          console.error('[serial-gate]', err);
          showStatus('error', settings.errorInvalid);
          isValidating = false;
          submitBtn.disabled = false;
          submitBtn.style.pointerEvents = '';
          input.disabled = false;
        });
    }

    function lockBuyButtons() {
      // Use body-level class — CSS overlay blocks all interaction
      // This is Alpine-proof: doesn't fight with :disabled bindings
      document.body.classList.add('serial-gate-active');
    }

    function unlockBuyButtons() {
      document.body.classList.remove('serial-gate-active');
    }

    function disableForm() {
      input.disabled = true;
      submitBtn.disabled = true;
    }

    function showStatus(type, message) {
      statusEl.textContent = message;
      statusEl.className = 'serial-gate__status';
      if (type === 'success') statusEl.classList.add('serial-gate__status--success');
      else if (type === 'error') statusEl.classList.add('serial-gate__status--error');
      else if (type === 'loading') statusEl.classList.add('serial-gate__status--loading');
    }
  }

  // --- Quantity cap (一人3点まで) ---

  function capQuantityInputs(max) {
    var inputs = document.querySelectorAll('input[name="quantity"], [data-product-form] input[type="number"]');
    inputs.forEach(function (q) {
      q.setAttribute('max', String(max));
      var clamp = function () {
        var v = parseInt(q.value, 10);
        if (!isNaN(v) && v > max) q.value = String(max);
      };
      q.addEventListener('change', clamp);
      q.addEventListener('input', clamp);
    });
  }

  // --- Storefront API validation ---

  function validateCode(code, token, shopDomain, apiVersion, variantGid) {
    var endpoint = 'https://' + shopDomain + '/api/' + apiVersion + '/graphql.json';
    var headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token
    };

    var cartInput = variantGid
      ? '(input: {lines: [{merchandiseId: "' + variantGid + '", quantity: 1}]})'
      : '';
    return sfFetch(endpoint, headers, {
      query: 'mutation { cartCreate' + cartInput + ' { cart { id } userErrors { field message } } }'
    }).then(function (data) {
      var cart = data.data && data.data.cartCreate && data.data.cartCreate.cart;
      if (!cart || !cart.id) {
        throw new Error('Failed to create cart');
      }
      return cart.id;
    }).then(function (cartId) {
      return sfFetch(endpoint, headers, {
        query: 'mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) { cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) { cart { id discountCodes { code applicable } } userErrors { field message } } }',
        variables: { cartId: cartId, discountCodes: [code] }
      });
    }).then(function (data) {
      var result = data.data && data.data.cartDiscountCodesUpdate;
      if (!result || !result.cart) {
        throw new Error('Discount validation failed');
      }
      var codes = result.cart.discountCodes || [];
      var matched = codes.find(function (c) {
        return c.code.toUpperCase() === code.toUpperCase();
      });
      return { applicable: matched ? matched.applicable : false };
    });
  }

  function sfFetch(endpoint, headers, body) {
    return fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body)
    }).then(function (resp) {
      if (!resp.ok) throw new Error('Storefront API error: ' + resp.status);
      return resp.json();
    });
  }

  function getSettings(section) {
    return {
      successMessage: section.dataset.msgSuccess || '認証されました。購入手続きへお進みください。',
      errorInvalid: section.dataset.msgErrorInvalid || 'このコードは無効です。入力内容をご確認ください。',
      errorUsed: section.dataset.msgErrorUsed || 'このコードは使用済みまたは有効期間外です。',
      errorEmpty: section.dataset.msgErrorEmpty || 'シリアルコードを入力してください。'
    };
  }
})();